import { Component, ElementRef, EventEmitter, HostListener, Input, Output, SimpleChange, SimpleChanges, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SummaryData } from 'src/app/pages/new-service/interfaces/SummaryData';
import { CustomFieldsService } from './services/custom-fields.service';

export enum CustomFieldType {
  STRING = "string",
  BOOLEAN = "bool",
}


export interface FunnelCustomField {
  Id: string;
  Title: string;
  Description: string;
  Type: CustomFieldType;
  MaxLength?: number;
  Value?: string;
}

export const CustomFieldTagColorsBackground = [
  {
    bkg: "var(--purple-100, #EDE9FE)",
    text: "var(--purple-800, #5B21B6)"
  },
  {
    bkg: "#FEF3C7",
    text: "#92400E"
  },
  {
    bkg: "var(--Grey-100, #E7E7E7)",
    text: "var(--Grey-800, #515151)"
  },
  {
    bkg: "var(--Green-cafler-100, #DEFEEB)",
    text: "#065F46"
  },
  {
    bkg: "#FFDDC8",
    text: "#B94E0B"
  },
  {
    bkg: "#FEE2E2",
    text: "#991B1B"
  },
  {
    bkg: "#DBEAFE",
    text: "#1E40AF"
  },
  {
    bkg: "#FCE7F3",
    text: "#9D174D"
  }

]


@Component({
  selector: 'app-custom-fields-manager',
  templateUrl: './custom-fields-manager.component.html',
  styleUrls: ['./custom-fields-manager.component.scss']
})
export class CustomFieldsManagerComponent {
  @Input() showTitle: boolean = false;
  @Input() showDescription: boolean = false;
  @Input() SummaryData: SummaryData = {} as SummaryData;
  @Output() updateSummaryDataEmitter = new EventEmitter<SummaryData>()
  @Output() customFieldsValidEmitter = new EventEmitter<boolean>()

  TagColorsBackground = CustomFieldTagColorsBackground;
  CustomFileds: any;
  isOptionSelected = false;
  OpenDropdown: boolean = false;
  SelectedCustomFieldsIds: string[] = []
  IsSomeSelectedFieldIsString: boolean = false;
  form: FormGroup;
  customFieldsFormArray: FormArray;

  @ViewChild('dropdownInput', { static: false }) dropdownInputRef!: ElementRef;
  @ViewChild('dropdownWrapper', { static: false }) dropdownWrapperRef!: ElementRef;

  constructor(private fb: FormBuilder, private CustomFieldsService: CustomFieldsService) {

    this.CustomFieldsService.getCustomFields().subscribe((data: any) => {
      this.CustomFileds = data;
    })


    this.customFieldsFormArray = this.fb.array([]);
    this.form = this.fb.group({
      customFields: this.customFieldsFormArray
    });
    this.customFieldsFormArray.valueChanges.subscribe(() => this.updateCustomFieldsEmit());
  }


  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent): void {
    const clickedInsideInput = this.dropdownInputRef?.nativeElement.contains(event.target);
    const clickedInsideWrapper = this.dropdownWrapperRef?.nativeElement.contains(event.target);

    if (!clickedInsideInput && !clickedInsideWrapper) {
      this.OpenDropdown = false;
    }
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['SummaryData'] && changes['SummaryData'].currentValue !== changes['SummaryData'].previousValue) {

      const fields = this.SummaryData?.CustomFields ?? [];

      this.customFieldsFormArray.clear();

      fields.forEach((field: FunnelCustomField) => {
        const initialValue = field.Value ?? (field.Type === CustomFieldType.BOOLEAN ? "true" : '');
        const validators = field.Type === CustomFieldType.STRING
          ? [Validators.required, Validators.maxLength(field.MaxLength || 25)]
          : [];
        const controlGroup = this.fb.group({
          Id: [field.Id],
          Title: [field.Title],
          Description: [field.Description],
          Type: [field.Type],
          Value: [initialValue, validators],
          MaxLength: field.Type === CustomFieldType.STRING ? [field.MaxLength || 25] : 0
        });

        this.customFieldsFormArray.push(controlGroup);
      });

      this.sortCustomFieldsFormArray();
      this.updateSomeSelectedFieldIsString();
      this.updateCustomFieldsSelected();
    }
  }

  get selectedCustomFields(): FunnelCustomField[] {
    return this.customFieldsFormArray.controls.map(ctrl => ctrl.value);
  }



  selectCustomField(customField: FunnelCustomField) {
    const index = this.customFieldsFormArray.controls.findIndex(ctrl => ctrl.get('Id')?.value === customField.Id);

    if (index >= 0) {
      this.customFieldsFormArray.removeAt(index);
    } else {
      const initialValue = customField.Value ?? (customField.Type === CustomFieldType.BOOLEAN ? "true" : '');
      const validators = customField.Type === CustomFieldType.STRING
        ? [Validators.required, Validators.maxLength(customField.MaxLength || 25)]
        : [];
      const group = this.fb.group({
        Id: [customField.Id],
        Title: [customField.Title],
        Description: [customField.Description],
        Type: [customField.Type],
        Value: [initialValue, validators],
        MaxLength: customField.Type === CustomFieldType.STRING ? [customField.MaxLength || 25] : 0
      });

      this.customFieldsFormArray.push(group);
    }

    this.sortCustomFieldsFormArray();
    this.updateSomeSelectedFieldIsString();
    this.updateCustomFieldsSelected();
    this.updateCustomFieldsEmit();
  }


  sortCustomFieldsFormArray() {
    const sorted = [...this.customFieldsFormArray.controls].sort((a, b) => {
      const aType = a.get('Type')?.value;
      const bType = b.get('Type')?.value;

      if (aType === CustomFieldType.STRING && bType !== CustomFieldType.STRING) return -1;
      if (aType !== CustomFieldType.STRING && bType === CustomFieldType.STRING) return 1;
      return 0;
    });

    this.customFieldsFormArray.clear();
    sorted.forEach(ctrl => this.customFieldsFormArray.push(ctrl));
  }

  markFormTouched() {
    this.markAllControlsTouched(this.form);
  }

  markAllControlsTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if ((control as FormGroup).controls) {
        this.markAllControlsTouched(control as FormGroup);
      }
    });
  }

  updateSomeSelectedFieldIsString() {
    this.IsSomeSelectedFieldIsString = this.customFieldsFormArray.controls.some(ctrl => ctrl.get('Type')?.value === CustomFieldType.STRING);
  }

  updateCustomFieldsSelected() {
    this.SelectedCustomFieldsIds = this.customFieldsFormArray.controls.map(ctrl => ctrl.get('Id')?.value);
  }


  removeSelectedOption(option: FunnelCustomField, event: Event) {
    event.stopPropagation();

    const index = this.customFieldsFormArray.controls.findIndex(
      ctrl => ctrl.get('Id')?.value === option.Id
    );

    if (index >= 0) {
      this.customFieldsFormArray.removeAt(index);
      this.updateSomeSelectedFieldIsString();
      this.updateCustomFieldsSelected();
      this.updateCustomFieldsEmit();
    }
  }


  updateCustomFieldsEmit() {
    const newSummaryData: SummaryData = {
      ...this.SummaryData,
      CustomFields: this.customFieldsFormArray.value
    };
    this.customFieldValidEmit();
    this.updateSummaryDataEmitter.emit(newSummaryData);
  }

  customFieldValidEmit() {
    this.customFieldsValidEmitter.emit(this.customFieldsFormArray.valid);
  }
}
